import {IssueGroupingKey, StoppageCode, usePlantStoppagesDetails} from '@domain/stoppages'
import {DateRange} from '@hconnect/uikit/src/common'
import {isSameDate} from '@hconnect/uikit/src/common/utils/dateUtils'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box, FormControlLabel, Grid, Paper, Switch, Typography} from '@mui/material'
import {TFunction} from 'i18next'
import React, {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, useParams} from 'react-router'

import {
  StatusPageHeader,
  DowntimeFilters,
  StoppageDetailsTable,
  TopIssuesByCode
} from '../components'
import {
  DOWNTIME_DETAILS_FILTER_OPTIONS,
  DowntimeAnalyticsEventName,
  FEATURE_NAMESPACE
} from '../consts'
import {
  useDowntimeFiltersState,
  usePlantTimeRange,
  useTimeRangeParam,
  useTrackDowntimeDashboardAnalytics
} from '../hooks'
import {useDowntimePlantId, useStoppagesStatsTimeRange} from '../providers'
import {getBarTimeFormattedLabelTitle, getTimeStepForTitle, isUnavailable} from '../utils'

type PathParameter = {
  groupId: string
  groupType: IssueGroupingKey
}

type DefaultStoppageDetailsProps = {
  backToStoppagesBasePath: string
  stoppageConfig?: StoppageCode[]
}

const getTheTitle = ({
  stoppageCodes,
  groupKey,
  groupId,
  timeRange,
  language,
  translationFn,
  timezone
}: {
  stoppageCodes: StoppageCode[] | undefined
  groupKey: IssueGroupingKey
  groupId: string
  timeRange: DateRange<Date>
  language: string
  translationFn: TFunction
  timezone: string
}) => {
  if (groupKey === IssueGroupingKey.STOPPAGE_CODE) {
    const stoppageCode = stoppageCodes?.find((stoppageCode) => stoppageCode.code === groupId)
    const text = isUnavailable(groupId)
      ? translationFn('label.stoppageTypeOther')
      : stoppageCode?.text
    return text ?? groupId
  }
  if (groupKey === IssueGroupingKey.TIME_INTERVAL) {
    const timeStepForTitle = getTimeStepForTitle(timeRange, timezone)
    return getBarTimeFormattedLabelTitle(
      timeStepForTitle,
      timeRange.from.toISOString(),
      timeRange.to.toISOString(),
      language,
      timezone
    )
  }
  return groupId
}

export const DefaultStoppageDetails: React.FC<DefaultStoppageDetailsProps> = ({
  stoppageConfig,
  backToStoppagesBasePath
}) => {
  const plantId = useDowntimePlantId()
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const trackAnalyticsEvent = useTrackDowntimeDashboardAnalytics(plantId)
  const {groupType, groupId} = useParams<PathParameter>()
  if (!groupId || !groupType) throw new Error('Missing stoppage group prop')
  const timezone = useUpmPlantTimezone(plantId)
  const timeRange = usePlantTimeRange()
  const [, setTimeRange] = useTimeRangeParam()
  const {stoppagesStatsTimeRange} = useStoppagesStatsTimeRange()
  const setTimeRangeFilter = useCallback(() => {
    if (stoppagesStatsTimeRange && groupType === IssueGroupingKey.TIME_INTERVAL) {
      setTimeRange(stoppagesStatsTimeRange)
    }
  }, [groupType, setTimeRange, stoppagesStatsTimeRange])

  const {
    mainEquipments,
    setMainEquipments,
    equipmentNumbers,
    setEquipmentNumbers,
    failureStoppage,
    setFailureStoppage,
    stoppageCodes,
    setStoppageCodes
  } = useDowntimeFiltersState({groupId, groupType})

  const [showDescriptions, setShowDescriptions] = useState(false)

  const selectedMainEquipments = useMemo(() => mainEquipments?.map((eq) => eq.id), [mainEquipments])
  const selectedEquipments = useMemo(() => equipmentNumbers?.map((eq) => eq.id), [equipmentNumbers])

  const {
    data: stoppagesDetails = [],
    isLoading: isStoppageDetailsLoading,
    isError: isStoppageDetailsError,
    refetch: refetchStoppageDetails,
    isRefetching: isStoppageDetailsRefetching
  } = usePlantStoppagesDetails(plantId, {
    ...timeRange,
    selectedMainEquipments,
    selectedStoppages: failureStoppage,
    selectedEquipments,
    selectedStoppageCodes: stoppageCodes
  })

  const timeFramesTitle = useMemo(() => {
    const singleDate = isSameDate(timeRange.from, timeRange.to)
    return timeRange.quickSelectionId
      ? `${t(`quickSelection.${timeRange.quickSelectionId}`)}`
      : `${timeRange.from.toDateString()}${singleDate ? '' : ` - ${timeRange.to.toDateString()}`}`
  }, [timeRange, t])

  return (
    <>
      <StatusPageHeader
        title={getTheTitle({
          stoppageCodes: stoppageConfig,
          groupKey: groupType,
          groupId,
          timeRange,
          language,
          translationFn: t,
          timezone
        })}
        backButtonProps={{
          target: generatePath(backToStoppagesBasePath, {plantId}),
          targetName: t('actions.backToStoppages'),
          onClick: setTimeRangeFilter
        }}
        filterComponent={
          <DowntimeFilters
            filteringEventName={DowntimeAnalyticsEventName.UserAdjusts3LevelFilter}
            failureState={{failureStoppage, setFailureStoppage}}
            mainEquipmentState={{mainEquipments, setMainEquipments}}
            stoppageCodesState={{stoppageCodes, setStoppageCodes}}
            equipmentNumbersState={{equipmentNumbers, setEquipmentNumbers}}
            options={DOWNTIME_DETAILS_FILTER_OPTIONS[groupType]}
          />
        }
      />

      <Grid container spacing={2}>
        <Grid item md={12} xs={12}>
          <TopIssuesByCode
            stoppageConfig={stoppageConfig}
            selectedEquipments={selectedMainEquipments}
            selectedEquipmentNumbers={selectedEquipments}
            selectedStoppages={failureStoppage}
            selectedStoppageCodes={stoppageCodes}
            title={t('label.downtimes')}
            issueCount={10}
          />
        </Grid>
        <Grid item md={12} xs={12}>
          <Paper sx={{padding: (theme) => theme.spacing(1)}}>
            <Box display={'flex'} justifyContent={'space-between'} mb={1}>
              <Box display="flex" m={2} flexDirection="column">
                <Typography variant="subtitle1">{timeFramesTitle}</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={showDescriptions}
                    data-test-id="events-show-descriptions-toggle"
                    onChange={() => {
                      setShowDescriptions(!showDescriptions)
                      trackAnalyticsEvent(
                        DowntimeAnalyticsEventName.UserAdjustDescriptionColumn,
                        {}
                      )
                    }}
                    color="primary"
                  />
                }
                label={t('actions.toggleDescriptions')}
              />
            </Box>

            <StoppageDetailsTable
              refetch={() => void refetchStoppageDetails()}
              isRefetching={isStoppageDetailsRefetching}
              isLoading={isStoppageDetailsLoading}
              isError={isStoppageDetailsError}
              stoppagesDetails={stoppagesDetails}
              showDescriptions={showDescriptions}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  )
}
